export function calculateHaversineDistanceKm(start = {}, end = {}) {
  const startLng = Number(start?.longitude ?? start?.lng);
  const startLat = Number(start?.latitude ?? start?.lat);
  const endLng = Number(end?.longitude ?? end?.lng);
  const endLat = Number(end?.latitude ?? end?.lat);
  if (
    !Number.isFinite(startLng) ||
    !Number.isFinite(startLat) ||
    !Number.isFinite(endLng) ||
    !Number.isFinite(endLat)
  ) {
    return 0;
  }

  const earthRadiusKm = 6371.0088;
  const toRadians = (value) => (value * Math.PI) / 180;
  const latitudeDelta = toRadians(endLat - startLat);
  const longitudeDelta = toRadians(endLng - startLng);
  const startLatitude = toRadians(startLat);
  const endLatitude = toRadians(endLat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;

  return (
    earthRadiusKm *
    2 *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

export function getRouteGeometryLines(geometry = {}) {
  if (String(geometry?.type || "") === "LineString") {
    return Array.isArray(geometry?.coordinates) ? [geometry.coordinates] : [];
  }
  if (String(geometry?.type || "") === "MultiLineString") {
    return Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
  }
  return [];
}

export function projectPointOnSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy || 1;
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    ),
  );
  return {
    x: start.x + ratio * dx,
    y: start.y + ratio * dy,
    ratio,
  };
}

export function indexToAlphaLabel(index = 0) {
  let value = Number(index) + 1;
  if (!Number.isFinite(value) || value < 1) return "A";
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

export function normalizePointType(pointType = "") {
  const value = String(pointType || "")
    .trim()
    .toLowerCase();
  if (value === "parking" || value === "restaurant" || value === "hotel")
    return value;
  return "building";
}

export function distance2D(a, b) {
  const dx = Number(a?.lng ?? 0) - Number(b?.lng ?? 0);
  const dy = Number(a?.lat ?? 0) - Number(b?.lat ?? 0);
  return Math.sqrt(dx * dx + dy * dy);
}

export function orderBoundaryPointsByShape(rawPoints = [], previousPoints = []) {
  const source = Array.isArray(rawPoints) ? rawPoints : [];
  if (source.length <= 2) return source;

  const byId = new Map(
    source
      .map((point) => [String(point?.id || ""), point])
      .filter(([id]) => id),
  );
  if (!byId.size) return source;

  const ordered = (Array.isArray(previousPoints) ? previousPoints : [])
    .map((point) => byId.get(String(point?.id || "")))
    .filter(Boolean);

  const remaining = source.filter(
    (point) =>
      !ordered.some(
        (item) => String(item?.id || "") === String(point?.id || ""),
      ),
  );

  if (!ordered.length) return source;

  const insertBetweenBestEdge = (list, point) => {
    if (list.length < 2) {
      list.push(point);
      return list;
    }
    let bestInsertIndex = list.length;
    let bestCost = Number.POSITIVE_INFINITY;
    for (let i = 0; i < list.length; i += 1) {
      const current = list[i];
      const next = list[(i + 1) % list.length];
      const cost =
        distance2D(current, point) +
        distance2D(point, next) -
        distance2D(current, next);
      if (cost < bestCost) {
        bestCost = cost;
        bestInsertIndex = i + 1;
      }
    }
    list.splice(bestInsertIndex, 0, point);
    return list;
  };

  remaining.forEach((point) => {
    insertBetweenBestEdge(ordered, point);
  });

  return ordered;
}
